import {
  Box,
  Button,
  EmptyState,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  Menu,
  Portal,
  Table,
  Text,
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuContactRound,
  LuListFilter,
  LuPlus,
  LuSearch,
  LuUpload,
} from 'react-icons/lu'
import {
  createContactBook,
  deleteContactBook,
  getBankById,
  getBanks,
  getContactBooks,
  updateContactBook,
} from '@/api/settings.api'
import { getOrders } from '@/api/orders.api'
import type { ContactBookDto } from '@/api/dto/settings.dto'
import { BankAdvisorDrawer } from '@/components/modals/BankAdvisorDrawer'
import { ContactImportDrawer } from '@/components/modals/ContactImportDrawer'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { useUserStore } from '@/stores/user.store'
import { FormLayout } from '@/components/layout/FormLayout'

const UNION_BANK_ID = 1
const PAGE_SIZE = 10
const CONTACT_BOOK_FETCH_PAGE_SIZE = 100

type SortField = 'name' | 'advisorNumber' | 'customerName'
type SortDir = 'asc' | 'desc'

type AdvisorRow = ContactBookDto & {
  name: string
  customerName: string
}

export function BankAdvisorSettings() {
  const user = useUserStore(s => s.user)
  const didLoadForUserRef = useRef<string | null>(null)
  const loadErrorShownRef = useRef(false)
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<ContactBookDto[]>([])
  const [customerOptions, setCustomerOptions] = useState<Array<{ label: string; value: string }>>(
    []
  )
  /**
   * Names for *every* Leistungsempfänger this SIB can see, used only to label the list.
   * `customerOptions` is deliberately narrowed to banks with an approved order — that is the right
   * set to offer when creating a Berater, but too narrow to read names from: a contact at any
   * other bank (a bulk import creates them freely) then fell back to showing "ID 297".
   */
  const [entityNames, setEntityNames] = useState<Map<number, string>>(new Map())
  const [selectedAdvisor, setSelectedAdvisor] = useState<ContactBookDto | null>(null)

  async function loadData() {
    if (!user?.id) return

    try {
      setLoading(true)

      const currentYear = new Date().getFullYear()
      const [contactBookResult, banksResult, ordersResult] = await Promise.allSettled([
        getContactBooks({
          userId: user.id,
          bankId: UNION_BANK_ID,
          page: 1,
          pageSize: CONTACT_BOOK_FETCH_PAGE_SIZE,
        }),
        getBanks(),
        getOrders({ year: currentYear }),
      ])

      if (contactBookResult.status === 'fulfilled') {
        setItems(contactBookResult.value.items ?? [])
      } else {
        console.error('Failed to load contact books', contactBookResult.reason)
        setItems([])
        if (!loadErrorShownRef.current) {
          toast.error('Berater konnten nicht geladen werden.')
          loadErrorShownRef.current = true
        }
      }

      if (banksResult.status !== 'fulfilled') {
        throw banksResult.reason
      }

      const banksRes = banksResult.value
      const unionBank = (banksRes.items ?? []).find(bank =>
        (bank.name ?? '').toLowerCase().includes('union')
      )

      const unionDetails = unionBank ? await getBankById(unionBank.id) : null
      const topLevelBanks = (banksRes.items ?? []).filter(bank => bank.active !== false)

      const allEntities = unionDetails?.associatedEntities ?? []

      const nameById = new Map<number, string>()
      for (const bank of topLevelBanks) nameById.set(Number(bank.id), bank.name)
      for (const entity of allEntities) nameById.set(Number(entity.id), entity.name)
      setEntityNames(nameById)

      if (ordersResult.status === 'fulfilled') {
        const ordersRes = ordersResult.value
        const allOrders: any[] = Object.values((ordersRes as any).items ?? {})
          .flatMap(yearObj => Object.values(yearObj as any))
          .flat()
          .filter(order => order && typeof order === 'object')

        const approvedOrders = allOrders.filter(
          order => order.status === 'APPROVED' && order.userId === user.id
        )

        const allowedEntityIds = new Set<number>()
        for (const order of approvedOrders) {
          const bankId = Number((order as any).bankId)
          const customerId = Number((order as any).customerId)

          if (bankId === UNION_BANK_ID) {
            if (Number.isFinite(customerId) && customerId > 0) allowedEntityIds.add(customerId)
            continue
          }

          if (Number.isFinite(bankId) && bankId > 0) allowedEntityIds.add(bankId)
        }

        const allowedEntities = allEntities.filter(entity =>
          allowedEntityIds.has(Number(entity.id))
        )

        const mappedAllowedEntities = allowedEntities.map(entity => ({
          label: entity.name,
          value: String(entity.id),
        }))

        setCustomerOptions(
          mappedAllowedEntities.length > 0
            ? mappedAllowedEntities
            : topLevelBanks.map(bank => ({
                label: bank.name,
                value: String(bank.id),
              }))
        )
      } else {
        console.error('Failed to load orders for advisor customer filtering', ordersResult.reason)
        const mappedEntities = allEntities.map(entity => ({
          label: entity.name,
          value: String(entity.id),
        }))

        setCustomerOptions(
          mappedEntities.length > 0
            ? mappedEntities
            : topLevelBanks.map(bank => ({
                label: bank.name,
                value: String(bank.id),
              }))
        )
      }
    } catch (error) {
      console.error('Failed to load advisor settings context', error)
      if (!loadErrorShownRef.current) {
        toast.error('Berater konnten nicht geladen werden.')
        loadErrorShownRef.current = true
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!user?.id) return
    if (didLoadForUserRef.current === user.id) return
    didLoadForUserRef.current = user.id
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const customerNameById = useMemo(
    () => new Map(customerOptions.map(option => [Number(option.value), option.label])),
    [customerOptions]
  )

  const rows = useMemo<AdvisorRow[]>(() => {
    const normalizedSearch = search.trim().toLowerCase()
    const dir = sortDir === 'asc' ? 1 : -1

    let list = items.map(item => ({
      ...item,
      name: `${item.firstName} ${item.lastName}`.trim(),
      customerName:
        entityNames.get(item.customerId) ??
        customerNameById.get(item.customerId) ??
        `ID ${item.customerId}`,
    }))

    if (normalizedSearch) {
      list = list.filter(item =>
        [item.name, item.advisorNumber, item.customerName].some(value =>
          value.toLowerCase().includes(normalizedSearch)
        )
      )
    }

    list.sort((a, b) => {
      const aValue =
        sortField === 'advisorNumber'
          ? a.advisorNumber
          : sortField === 'customerName'
            ? a.customerName
            : a.name
      const bValue =
        sortField === 'advisorNumber'
          ? b.advisorNumber
          : sortField === 'customerName'
            ? b.customerName
            : b.name

      return aValue.localeCompare(bValue, 'de', { numeric: true }) * dir
    })

    return list
  }, [customerNameById, entityNames, items, search, sortDir, sortField])

  /**
   * Options for the drawer. `customerOptions` only holds banks this SIB has an approved order
   * for, which is the right set to offer for a *new* Berater but leaves an existing one unable to
   * show its own Leistungsempfänger: the Select finds no matching item and falls back to the
   * placeholder, so the edit view looked empty. A bulk import creates Berater at any bank the SIB
   * can see, so this is the normal case for imported rows, not an edge case.
   *
   * The two scopes differ on purpose (decided 14.10.2026): the import stays permissive because a
   * contact book is preparation, while the create form keeps offering only banks with an approved
   * order. So this is not a gap to close by widening `customerOptions` — the narrow list is the
   * intended set of choices for a *new* Berater.
   */
  const drawerCustomerOptions = useMemo(() => {
    if (!selectedAdvisor) return customerOptions

    const currentValue = String(selectedAdvisor.customerId)
    if (customerOptions.some(option => option.value === currentValue)) return customerOptions

    return [
      ...customerOptions,
      {
        label: entityNames.get(selectedAdvisor.customerId) ?? `ID ${selectedAdvisor.customerId}`,
        value: currentValue,
      },
    ]
  }, [customerOptions, entityNames, selectedAdvisor])

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const pagedRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, rows]
  )

  useEffect(() => {
    setPage(1)
  }, [search, sortField, sortDir])

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  async function handleCreate(values: {
    customerId: number
    firstName: string
    lastName: string
    advisorNumber: string
  }) {
    try {
      setSubmitting(true)
      await createContactBook({
        bankId: UNION_BANK_ID,
        ...values,
      })
      toast.success('Berater wurde angelegt.')
      setDrawerOpen(false)
      await loadData()
    } catch (error) {
      console.error('Failed to create contact book entry', error)
      toast.error('Berater konnte nicht angelegt werden.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleUpdate(values: {
    customerId: number
    firstName: string
    lastName: string
    advisorNumber: string
  }) {
    if (!selectedAdvisor?.id) return

    try {
      setSubmitting(true)
      await updateContactBook(selectedAdvisor.id, {
        bankId: UNION_BANK_ID,
        ...values,
      })
      toast.success('Berater wurde aktualisiert.')
      setDrawerOpen(false)
      setSelectedAdvisor(null)
      await loadData()
    } catch (error) {
      console.error('Failed to update contact book entry', error)
      toast.error('Berater konnte nicht aktualisiert werden.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(id: number) {
    try {
      setSubmitting(true)
      await deleteContactBook(id)
      toast.success('Berater wurde gelöscht.')
      setDrawerOpen(false)
      setSelectedAdvisor(null)
      await loadData()
    } catch (error) {
      console.error('Failed to delete contact book entry', error)
      toast.error('Berater konnte nicht gelöscht werden.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Flex direction="column" gap={8}>
        <Text>Berater werden geladen...</Text>
      </Flex>
    )
  }

  return (
    <FormLayout.Root>
      <Flex gap={6} align="flex-start">
        <FormLayout.Root flex="1" minW={0}>
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Berater</FormLayout.Title>
            <FormLayout.Description>
              Hinterlegen Sie die Beraternummern in Ihrem Kontaktbuch, um sie beim Anlegen von
              Aktivitäten schnell und einfach auszuwählen.
            </FormLayout.Description>
          </Flex>

          <Flex direction="column" gap={4}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'stretch', md: 'center' }}
              justify="space-between"
              gap={2}
            >
              <Flex
                direction={{ base: 'column', sm: 'row' }}
                align="stretch"
                gap={3}
                flex="1"
                minW={0}
              >
                <InputGroup minW={0} flex="1" startElement={<LuSearch />}>
                  <Input
                    placeholder="Berater durchsuchen"
                    value={search}
                    w="full"
                    onChange={e => setSearch(e.target.value)}
                  />
                </InputGroup>

                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button variant="subtle">
                      <HStack gap={2}>
                        <LuListFilter />
                        <Text>Sortierung</Text>
                      </HStack>
                    </Button>
                  </Menu.Trigger>

                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content minW="280px">
                        <Menu.Item
                          value="name"
                          closeOnSelect={false}
                          onClick={() => setSortField('name')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Alphabetisch</Text>
                            {sortField === 'name' && <LuCheck />}
                          </Flex>
                        </Menu.Item>
                        <Menu.Item
                          value="advisorNumber"
                          closeOnSelect={false}
                          onClick={() => setSortField('advisorNumber')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Beraternummer</Text>
                            {sortField === 'advisorNumber' && <LuCheck />}
                          </Flex>
                        </Menu.Item>
                        <Menu.Item
                          value="customerName"
                          closeOnSelect={false}
                          onClick={() => setSortField('customerName')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Leistungsempfänger</Text>
                            {sortField === 'customerName' && <LuCheck />}
                          </Flex>
                        </Menu.Item>

                        <Menu.Separator />
                        <Menu.Item
                          value="asc"
                          closeOnSelect={false}
                          onClick={() => setSortDir('asc')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Aufsteigend</Text>
                            {sortDir === 'asc' && <LuCheck />}
                          </Flex>
                        </Menu.Item>

                        <Menu.Item
                          value="desc"
                          closeOnSelect={false}
                          onClick={() => setSortDir('desc')}
                        >
                          <Flex w="full" align="center" justify="space-between">
                            <Text>Absteigend</Text>
                            {sortDir === 'desc' && <LuCheck />}
                          </Flex>
                        </Menu.Item>
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              </Flex>

              <HStack gap={2}>
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <LuUpload />
                  <Text>Berater importieren</Text>
                </Button>

                <Button
                  variant="solid"
                  colorPalette="blue"
                  onClick={() => {
                    setSelectedAdvisor(null)
                    setDrawerOpen(true)
                  }}
                >
                  <LuPlus />
                  <Text>Neuen Berater anlegen</Text>
                </Button>
              </HStack>
            </Flex>

            <Box overflow="hidden">
              <Table.Root interactive overflow="hidden" minW="720px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader width="full">Berater</Table.ColumnHeader>
                    <Table.ColumnHeader
                      display={{ base: 'none', md: 'table-cell' }}
                      whiteSpace="nowrap"
                      width="1%"
                    >
                      Beraternummer
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      display={{ base: 'none', md: 'table-cell' }}
                      whiteSpace="nowrap"
                      width="1%"
                    >
                      Leistungsempfänger
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={3}>
                        <EmptyState.Root>
                          <EmptyState.Content>
                            <EmptyState.Indicator>
                              <LuContactRound />
                            </EmptyState.Indicator>
                            <EmptyState.Title>Keine Berater gefunden</EmptyState.Title>
                            <EmptyState.Description>
                              Legen Sie Ihren ersten Berater an, um Ihr Kontaktbuch aufzubauen.
                            </EmptyState.Description>
                            <Button
                              variant="solid"
                              colorPalette="blue"
                              onClick={() => {
                                setSelectedAdvisor(null)
                                setDrawerOpen(true)
                              }}
                            >
                              <LuPlus />
                              <Text>Neuen Berater anlegen</Text>
                            </Button>
                          </EmptyState.Content>
                        </EmptyState.Root>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    pagedRows.map(row => (
                      <Table.Row
                        key={row.id}
                        cursor="pointer"
                        onClick={() => {
                          setSelectedAdvisor(row)
                          setDrawerOpen(true)
                        }}
                      >
                        <Table.Cell>{row.name}</Table.Cell>
                        <Table.Cell
                          display={{ base: 'none', md: 'table-cell' }}
                          whiteSpace="nowrap"
                        >
                          {row.advisorNumber}
                        </Table.Cell>
                        <Table.Cell
                          display={{ base: 'none', md: 'table-cell' }}
                          whiteSpace="nowrap"
                        >
                          {row.customerName}
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            </Box>

            {totalPages > 2 && (
              <Flex p={3} justify="center" align="center" gap={2}>
                <IconButton
                  aria-label="prev"
                  variant="ghost"
                  disabled={page === 1}
                  onClick={() => setPage(current => Math.max(1, current - 1))}
                >
                  <LuChevronLeft />
                </IconButton>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map(currentPage => (
                  <Button
                    key={currentPage}
                    variant={currentPage === page ? 'solid' : 'outline'}
                    onClick={() => setPage(currentPage)}
                  >
                    {currentPage}
                  </Button>
                ))}
                <IconButton
                  aria-label="next"
                  variant="ghost"
                  disabled={page === totalPages}
                  onClick={() => setPage(current => Math.min(totalPages, current + 1))}
                >
                  <LuChevronRight />
                </IconButton>
              </Flex>
            )}
          </Flex>
        </FormLayout.Root>
      </Flex>

      <BankAdvisorDrawer
        open={drawerOpen}
        onOpenChange={open => {
          setDrawerOpen(open)
          if (!open) setSelectedAdvisor(null)
        }}
        mode={selectedAdvisor ? 'edit' : 'create'}
        initialAdvisor={selectedAdvisor}
        customerOptions={drawerCustomerOptions}
        onSubmit={selectedAdvisor ? handleUpdate : handleCreate}
        onDelete={handleDelete}
        isSubmitting={submitting}
      />

      <ContactImportDrawer
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={loadData}
      />
    </FormLayout.Root>
  )
}
